/**
 * KpiBand — Umsatz als Navy-Held (das EINE goldene Element des Screens:
 * die Umsatzzahl) + vier Kennzahlen mit Veränderung zur Vorperiode.
 */
import { memo } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { percentDelta } from '@/lib/dashboardAnalytics';
import { eur, eurShort, num } from './format';

function Delta({ current, previous, onDark = false }) {
  const pct = percentDelta(current, previous);
  if (pct === null) {
    return (
      <span className={cn('inline-flex items-center gap-1 text-xs', onDark ? 'text-white/50' : 'text-slate-400')}>
        <Minus className="w-3 h-3" /> kein Vergleich
      </span>
    );
  }
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const tone = onDark
    ? up ? 'bg-emerald-400/15 text-emerald-300' : 'bg-red-400/15 text-red-300'
    : up ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700';
  return (
    <span className={cn('inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums', tone)}>
      <Icon className="w-3.5 h-3.5" />
      {up ? '+' : ''}{pct.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %
    </span>
  );
}

const Sparkline = memo(function Sparkline({ points }) {
  return (
    <div className="h-16 -mx-1" aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="kpiSpark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" stopOpacity={0.22} />
              <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="revenue" stroke="rgba(255,255,255,0.75)" strokeWidth={1.5} fill="url(#kpiSpark)" isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

function Tile({ label, value, current, previous, hint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col justify-between gap-3 min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</p>
      <div>
        <p className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 tabular-nums truncate">{value}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Delta current={current} previous={previous} />
          {hint && <span className="text-xs text-slate-400 truncate">{hint}</span>}
        </div>
      </div>
    </div>
  );
}

export default function KpiBand({ loading, current, previous, newCount, prevNewCount, newContacts, series }) {
  if (loading) {
    return (
      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-3 md:gap-4" aria-busy="true">
        <Skeleton className="h-52 rounded-xl bg-slate-200" />
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[6.25rem] rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-[1.35fr_1fr] gap-3 md:gap-4">
      <div className="relative overflow-hidden rounded-xl bg-brand-950 text-white p-5 md:p-6 flex flex-col justify-between gap-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/50">Umsatz im Zeitraum</p>
            <p className="mt-2 font-display text-4xl md:text-[3.5rem] leading-none font-extrabold tracking-tight text-accent tabular-nums">
              {eurShort(current.revenue)}
            </p>
          </div>
          <Delta current={current.revenue} previous={previous.revenue} onDark />
        </div>
        <Sparkline points={series} />
        <p className="text-xs text-white/50 tabular-nums">
          Vorperiode {eur(previous.revenue)} · {num(current.count)} Bestellungen von {num(current.customers)} Kunden
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <Tile label="Bestellungen" value={num(current.count)} current={current.count} previous={previous.count} />
        <Tile label="Ø Bestellwert" value={eur(current.avgOrder)} current={current.avgOrder} previous={previous.avgOrder} />
        <Tile label="Aktive Kunden" value={num(current.customers)} current={current.customers} previous={previous.customers} />
        <Tile
          label="Neukunden"
          value={num(newCount)}
          current={newCount}
          previous={prevNewCount}
          hint={`${num(newContacts)} neue Kontakte`}
        />
      </div>
    </div>
  );
}
